import { useMemo } from "react";
import type { MatchEvent } from "../../../store/useMatchLogStore";

export interface FoulDisciplinaryStatus {
  foulCount: number;
  ejected: boolean;
  disqualified: boolean;
}

export interface UseDisciplinaryResult {
  foulStatus: Record<string, FoulDisciplinaryStatus>;
  foulCounts: Record<string, number>;
  expelledPlayerIds: Set<string>;
}

export const useDisciplinary = (
  liveEvents: MatchEvent[],
  queuedEvents: MatchEvent[],
): UseDisciplinaryResult => {
  const foulStatus = useMemo(() => {
    const byPlayer = new Map<
      string,
      { fouls: number; ejected: boolean; disqualified: boolean }
    >();

    const combinedEvents = [...liveEvents, ...queuedEvents];

    for (const event of combinedEvents) {
      const playerId = event.player_id;
      if (!playerId) continue;

      if (event.type === "FoulCommitted") {
        const state = byPlayer.get(playerId) ?? {
          fouls: 0,
          ejected: false,
          disqualified: false,
        };
        state.fouls += 1;
        byPlayer.set(playerId, state);
      } else if (event.type === "Ejected") {
        const state = byPlayer.get(playerId) ?? {
          fouls: 0,
          ejected: false,
          disqualified: false,
        };
        state.ejected = true;
        byPlayer.set(playerId, state);
      } else if (event.type === "Disqualified") {
        const state = byPlayer.get(playerId) ?? {
          fouls: 0,
          ejected: false,
          disqualified: false,
        };
        state.disqualified = true;
        byPlayer.set(playerId, state);
      }
    }

    const status: Record<string, FoulDisciplinaryStatus> = {};
    byPlayer.forEach((value, playerId) => {
      status[playerId] = {
        foulCount: value.fouls,
        ejected: value.ejected,
        disqualified: value.disqualified,
      };
    });
    return status;
  }, [liveEvents, queuedEvents]);

  const foulCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    Object.entries(foulStatus).forEach(([playerId, value]) => {
      counts[playerId] = value.foulCount;
    });
    return counts;
  }, [foulStatus]);

  const expelledPlayerIds = useMemo(() => {
    const expelled = new Set<string>();
    Object.entries(foulStatus).forEach(([playerId, status]) => {
      if (status.ejected || status.disqualified) {
        expelled.add(playerId);
      }
    });
    return expelled;
  }, [foulStatus]);

  return {
    foulStatus,
    foulCounts,
    expelledPlayerIds,
  };
};
